import { twoFactorService } from "../services/twoFactorService";
import { userService } from "../services/userService";

/**
 * Shared React Query definitions for the authenticated user's profile
 * (`/api/user`) and status (`/api/user/status`).
 *
 * Every consumer — hooks, providers, pages — MUST use these so the data is
 * fetched once and shared through the cache, instead of each component firing
 * its own request. Previously ~6 duplicate `/user` + `/user/status` calls went
 * out on load (UserDataLoader, ably-provider, LanguageProvider, useUser,
 * useUserType, plus page-level queries), because they used different/ad-hoc
 * query keys and raw `api.get` calls that bypassed the cache.
 *
 * Keys are scoped by Auth0 `sub` so a logout → login as a different user in the
 * same SPA session can't reuse the previous user's cached data.
 */

export const userQueryOptions = (sub: string | undefined) => ({
	queryKey: ["user", sub] as const,
	queryFn: () => userService.getUserWithDetails(),
	// Explicit staleTime so de-duplication never depends on the global QueryClient
	// default being applied (a hot-reload can keep an old staleTime:0 client, which
	// makes each consumer that mounts after the first fetch resolves re-fetch).
	// Profile data changes rarely; mutations that edit it should invalidate the
	// ["user"] key. 60s easily collapses the on-load fan-out to a single request.
	staleTime: 60_000,
});

export const userStatusQueryOptions = (sub: string | undefined) => ({
	queryKey: ["user", "status", sub] as const,
	queryFn: () => twoFactorService.getUserStatus(),
	// user_type / status are treated as immutable for the session: fetch once,
	// never auto-refetch, so a silent token renewal can't flip the isD2C gate.
	staleTime: Number.POSITIVE_INFINITY,
	gcTime: Number.POSITIVE_INFINITY,
});
